// The shortcut registry — single source of truth for bindings, categories and
// labels. Drives both the `?` shortcuts modal and the bottom hint bar. Handlers
// are attached at runtime (via `useShortcut`) where each action lives, so a
// shortcut listed here only fires once something registers a handler for its id.
//
// Authored IDENTICALLY across apps/web, apps/perps and marginx — keep in sync.

import type { Scope, ShortcutDef } from "./types";

export const SHORTCUTS: ShortcutDef[] = [
  // ---- Global ----
  { id: "global.shortcuts", keys: ["?"], scope: "global", category: "Global", label: "Keyboard shortcuts" },
  { id: "global.cancel", keys: ["esc"], scope: "global", category: "Global", label: "Cancel / close", allowInInput: true },
  { id: "global.palette", keys: ["mod+k"], scope: "global", category: "Global", label: "Command palette", allowInInput: true },
  { id: "global.search", keys: ["/"], scope: "global", category: "Global", label: "Focus search" },

  // ---- Go to (G chord) — marginx routes ----
  { id: "go.trade", keys: ["g", "t"], scope: "global", category: "Go to", label: "Trade" },
  { id: "go.portfolio", keys: ["g", "p"], scope: "global", category: "Go to", label: "Portfolio" },
  { id: "go.watchlist", keys: ["g", "w"], scope: "global", category: "Go to", label: "Watchlist" },
  { id: "go.leaderboard", keys: ["g", "b"], scope: "global", category: "Go to", label: "Leaderboard" },
  { id: "go.referrals", keys: ["g", "r"], scope: "global", category: "Go to", label: "Referrals" },
  { id: "go.history", keys: ["g", "h"], scope: "global", category: "Go to", label: "History" },
  { id: "go.settings", keys: ["g", "s"], scope: "global", category: "Go to", label: "Settings" },

  // ---- Trade (D chord; perps trade form scope) ----
  { id: "trade.buy", keys: ["d", "b"], scope: "trade", category: "Trade", label: "Long / Buy", labelWithoutPerps: "Buy", allowInInput: true },
  { id: "trade.sell", keys: ["d", "s"], scope: "trade", category: "Trade", label: "Short / Sell", labelWithoutPerps: "Sell", allowInInput: true },
  { id: "trade.market", keys: ["d", "m"], scope: "trade", category: "Trade", label: "Market order", allowInInput: true },
  { id: "trade.limit", keys: ["d", "l"], scope: "trade", category: "Trade", label: "Limit order", allowInInput: true },
  { id: "trade.size", keys: ["d", "z"], scope: "trade", category: "Trade", label: "Focus size", allowInInput: true },
  { id: "trade.price", keys: ["d", "p"], scope: "trade", category: "Trade", label: "Focus price", allowInInput: true },
  { id: "trade.reduceOnly", keys: ["d", "r"], scope: "trade", category: "Trade", label: "Toggle Reduce-Only", allowInInput: true, requiresInstrument: "PERPETUAL" },
  { id: "trade.focusForm", keys: ["d", "f"], scope: "trade", category: "Trade", label: "Go to order form", allowInInput: true },
  { id: "trade.leverage", keys: ["d", "v"], scope: "trade", category: "Trade", label: "Adjust leverage", allowInInput: true, requiresInstrument: "PERPETUAL" },
  { id: "trade.closePosition", keys: ["d", "c"], scope: "trade", category: "Trade", label: "Close position", allowInInput: true, requiresInstrument: "PERPETUAL" },
  { id: "trade.submit", keys: ["d", "enter"], scope: "trade", category: "Trade", label: "Place order", allowInInput: true },
  { id: "trade.cycleLeft", keys: ["left"], scope: "trade", category: "Trade", label: "Cycle order type ←" },
  { id: "trade.cycleRight", keys: ["right"], scope: "trade", category: "Trade", label: "Cycle order type →" },

  // ---- Tab navigation (page tab strips; "tabs" scope) ----
  { id: "tabs.prev", keys: ["["], scope: "tabs", category: "Navigation", label: "Previous tab" },
  { id: "tabs.next", keys: ["]"], scope: "tabs", category: "Navigation", label: "Next tab" },
  // Direct jump: 1–9 select the Nth tab of the hovered panel. The first binding
  // shows a single "1–9" row in the modal; the rest are hidden so the guide
  // isn't cluttered. Handlers read the pressed digit from the event, so all nine
  // share one registration per panel.
  ...Array.from({ length: 9 }, (_, i) => ({
    id: `tabs.jump${i + 1}`,
    keys: [`${i + 1}`],
    scope: "tabs" as Scope,
    category: "Navigation" as const,
    label: "Jump to tab (hovered panel)",
    displayKeys: i === 0 ? "1–9" : undefined,
    hidden: i !== 0,
  })),
];

/** Ordered ids for the 1–9 direct-jump bindings (see SHORTCUTS above). */
export const TAB_JUMP_IDS: string[] = Array.from(
  { length: 9 },
  (_, i) => `tabs.jump${i + 1}`,
);

/**
 * Curated, display-controlled hint-bar items, shown when their scope is active.
 * `requiresInstrument` hides an item on a build without that vertical, exactly
 * as it does for a SHORTCUTS entry.
 */
export const HINT_ITEMS: {
  scope: Scope;
  keys: string;
  label: string;
  requiresInstrument?: string;
}[] = [
  // { scope: "tabs", keys: "[ ]", label: "Switch tab (hovered panel)" },
  // { scope: "tabs", keys: "1–9", label: "Jump to tab" },
  { scope: "trade", keys: "D", label: "Trade" },
  { scope: "global", keys: "G", label: "Go" },
  { scope: "global", keys: "⌘K", label: "Search" },
  { scope: "global", keys: "?", label: "Shortcuts" },
];

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const TOKEN_GLYPH: Record<string, string> = {
  mod: isMac ? "⌘" : "Ctrl",
  shift: "⇧",
  alt: isMac ? "⌥" : "Alt",
  esc: "Esc",
  enter: "↵",
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  space: "Space",
  backspace: "⌫",
  del: "Del",
  tab: "Tab",
};

/** Pretty-print one chord token, e.g. "mod+k" → "⌘ K", "shift+space" → "⇧ Space". */
export function formatToken(token: string): string {
  return token
    .split("+")
    .map((part) => TOKEN_GLYPH[part] ?? (part.length === 1 ? part.toUpperCase() : part))
    .join(isMac ? "" : "+");
}

/** Pretty-print a full chord, e.g. ["g","e"] → "G E". */
export function formatChord(keys: string[]): string {
  return keys.map(formatToken).join(" ");
}
